import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaCheck,
  FaSpinner,
  FaBoxOpen,
  FaCalendarAlt,
  FaComments,
  FaCar,
  FaWrench,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { bookingService } from '../../services/apiService';
import { ticketService } from '../../services/ticketService';

const POLL_MS = 60_000;

function formatDateTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
}

function formatDateOnly(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

function formatSlotDisplay(slotDate, slotTime) {
  if (!slotDate) return null;
  const pretty = formatDateOnly(`${slotDate}T12:00:00`);
  if (!pretty) return slotTime ? `${slotDate} · ${slotTime}` : slotDate;
  return slotTime ? `${pretty} · ${slotTime}` : pretty;
}

function titleCase(value) {
  const s = String(value || '').trim();
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function ProgressVertical({ stages }) {
  return (
    <ol className="relative sm:hidden">
      {(stages || []).map((stage, idx) => {
        const isLast = idx === (stages?.length || 0) - 1;
        const complete = stage.isComplete;
        const current = stage.isCurrent;
        const ts = formatDateTime(stage.reachedAt);

        return (
          <li key={stage.id} className={`relative flex gap-3 ${!isLast ? 'pb-6' : ''}`}>
            {!isLast && (
              <span
                className={`absolute left-[13px] top-7 w-0.5 h-[calc(100%-0.75rem)] ${
                  complete ? 'bg-red-500' : 'bg-gray-200'
                }`}
                aria-hidden
              />
            )}
            <div className="relative shrink-0 z-10">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-semibold ${
                  complete
                    ? 'border-red-600 bg-red-600 text-white'
                    : current
                      ? 'border-red-500 bg-red-50 text-red-600 animate-pulse ring-2 ring-red-100'
                      : 'border-gray-200 bg-white text-gray-400'
                }`}
              >
                {complete ? <FaCheck className="text-[10px]" /> : idx + 1}
              </span>
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <p
                className={`text-sm font-medium leading-snug ${
                  current ? 'text-red-700' : complete ? 'text-gray-900' : 'text-gray-500'
                }`}
              >
                {stage.label}
                {current && (
                  <span className="ml-1.5 text-[11px] font-normal text-red-600">Current</span>
                )}
              </p>
              {ts && <p className="text-[11px] text-gray-500 mt-0.5">{ts}</p>}
              {!complete && !current && (
                <p className="text-[11px] text-gray-400 mt-0.5">Pending</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ProgressHorizontal({ stages }) {
  return (
    <div className="hidden sm:block overflow-x-auto -mx-1 px-1 pb-1">
      <ol className="flex min-w-[520px] items-start justify-between gap-1 md:min-w-0 md:gap-2">
        {(stages || []).map((stage, idx) => {
          const complete = stage.isComplete;
          const current = stage.isCurrent;
          const ts = formatDateTime(stage.reachedAt);
          const isLast = idx === (stages?.length || 0) - 1;

          return (
            <li key={stage.id} className="flex flex-1 min-w-0 flex-col items-center text-center">
              <div className="flex w-full items-center">
                {idx > 0 && (
                  <span
                    className={`h-0.5 flex-1 ${complete || current ? 'bg-red-500' : 'bg-gray-200'}`}
                    aria-hidden
                  />
                )}
                <span
                  className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold md:h-9 md:w-9 ${
                    complete
                      ? 'border-red-600 bg-red-600 text-white'
                      : current
                        ? 'border-red-500 bg-red-50 text-red-600 animate-pulse ring-4 ring-red-100'
                        : 'border-gray-200 bg-white text-gray-400'
                  }`}
                >
                  {complete ? <FaCheck className="text-[10px]" /> : idx + 1}
                </span>
                {!isLast && (
                  <span
                    className={`h-0.5 flex-1 ${
                      stages[idx + 1]?.isComplete || stages[idx + 1]?.isCurrent
                        ? 'bg-red-500'
                        : 'bg-gray-200'
                    }`}
                    aria-hidden
                  />
                )}
              </div>
              <p
                className={`mt-2 text-[11px] font-medium leading-tight md:text-xs ${
                  current ? 'text-red-700' : complete ? 'text-gray-900' : 'text-gray-500'
                }`}
              >
                {stage.label}
              </p>
              {current && (
                <span className="mt-0.5 text-[10px] text-red-600">Current</span>
              )}
              {ts && (
                <span className="mt-0.5 hidden text-[10px] text-gray-500 md:block">{ts}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ActionButtons({ onChat, onJobDetails, className = '' }) {
  return (
    <div className={`flex flex-col sm:flex-row gap-2 sm:gap-3 ${className}`}>
      <button
        type="button"
        onClick={onChat}
        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 min-h-[2.75rem] bg-white border border-gray-200 rounded-xl text-gray-800 text-sm font-medium hover:bg-gray-50 shadow-sm"
      >
        <FaComments className="text-red-500 shrink-0" />
        <span className="truncate">Contact garage</span>
      </button>
      <button
        type="button"
        onClick={onJobDetails}
        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 min-h-[2.75rem] bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 shadow-sm"
      >
        View job details
      </button>
    </div>
  );
}

const BookingStatus = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const loadStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await bookingService.getStatus(bookingId);
      setStatus(data);
    } catch (e) {
      if (!silent) {
        toast.error(e.message || 'Could not load booking status');
        setStatus(null);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [bookingId]);

  useEffect(() => {
    loadStatus(false);
  }, [loadStatus]);

  useEffect(() => {
    const id = setInterval(() => loadStatus(true), POLL_MS);
    return () => clearInterval(id);
  }, [loadStatus]);

  const handleSendMessage = async () => {
    const text = chatMessage.trim();
    if (text.length < 5) {
      toast.error('Please enter at least 5 characters.');
      return;
    }
    setChatSending(true);
    try {
      if (ticketId) {
        await ticketService.addComment(ticketId, { comment: text });
      } else {
        const ticket = await ticketService.createTicket({
          subject: `Garage booking ${status?.jobCardNumber || bookingId}`,
          description: text,
          category: 'Garage Service',
          priority: 'Medium',
          orderId: String(status?.jobCardId || bookingId),
        });
        const id = ticket?._id || ticket?.id || ticket?.ticketId;
        setTicketId(id);
      }
      toast.success('Message sent to garage team.');
      setChatMessage('');
      setChatOpen(false);
    } catch (e) {
      toast.error(e.message || 'Failed to send message');
    } finally {
      setChatSending(false);
    }
  };

  const openChat = () => setChatOpen(true);
  const openJobDetails = () => {
    if (status?.jobCardId) navigate(`/service/job/${status.jobCardId}`);
  };

  if (loading) {
    return (
      <div className="checkout-light-page min-h-[50vh] flex items-center justify-center bg-slate-50 px-4">
        <FaSpinner className="animate-spin text-3xl text-red-600" />
      </div>
    );
  }

  if (!status) {
    return (
      <div className="checkout-light-page min-h-[50vh] bg-slate-50 py-8 px-4 pb-28">
        <div className="max-w-lg mx-auto text-center">
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            Booking not found or you do not have access.
          </p>
          <button
            type="button"
            onClick={() => navigate('/service/jobs')}
            className="text-red-600 font-medium hover:text-red-700 text-sm"
          >
            ← Back to my jobs
          </button>
        </div>
      </div>
    );
  }

  const vehicleLabel = [status.vehicle?.make, status.vehicle?.model]
    .filter(Boolean)
    .join(' ');
  const reg = status.vehicle?.registrationNumber;

  return (
    <div className="checkout-light-page w-full min-h-[50vh] bg-slate-50 pt-2 pb-36 sm:pt-4 sm:pb-28 md:py-6 lg:py-8 px-3 sm:px-4 lg:px-8">
      <div className="max-w-2xl mx-auto w-full">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-red-600 hover:text-red-700 font-medium mb-3 sm:mb-4 inline-flex items-center gap-1 text-sm"
        >
          ← Back
        </button>

        <div className="flex items-start justify-between gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              Booking status
            </h1>
            <p className="text-gray-600 text-xs sm:text-sm mt-1 line-clamp-2">
              {status.garage?.displayName}
              {status.garage?.city ? ` · ${status.garage.city}` : ''}
            </p>
          </div>
          {refreshing && (
            <span className="text-[11px] sm:text-xs text-gray-500 flex items-center gap-1 shrink-0 pt-1">
              <FaSpinner className="animate-spin" /> Updating
            </span>
          )}
        </div>

        {/* Summary card */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-5 mb-3 sm:mb-4">
          <div className="grid grid-cols-1 gap-2.5 sm:gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
            <div className="flex items-start gap-2 text-gray-700 min-w-0">
              <FaCar className="text-red-500 shrink-0 mt-0.5" />
              <span className="break-words leading-snug">
                {vehicleLabel || 'Your vehicle'}
                {reg ? ` (${reg})` : ''}
              </span>
            </div>
            <div className="flex items-start gap-2 text-gray-700 min-w-0">
              <FaWrench className="text-red-500 shrink-0 mt-0.5" />
              <span className="break-words">{titleCase(status.serviceType) || 'Service'}</span>
            </div>
            {status.slotDate && (
              <div className="flex items-start gap-2 text-gray-700 min-w-0 sm:col-span-2 lg:col-span-1">
                <FaCalendarAlt className="text-red-500 shrink-0 mt-0.5" />
                <span className="break-words leading-snug">
                  {formatSlotDisplay(status.slotDate, status.slotTime)}
                </span>
              </div>
            )}
          </div>
          {status.jobCardNumber && (
            <p className="text-[11px] sm:text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100 break-all">
              Job ref: {status.jobCardNumber}
            </p>
          )}
        </div>

        {/* Progress stages */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-5 md:p-6 mb-3 sm:mb-4">
          <h2 className="text-xs sm:text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4 sm:mb-5">
            Progress
          </h2>
          <ProgressHorizontal stages={status.stages} />
          <ProgressVertical stages={status.stages} />
        </div>

        {/* Parts ETA banner */}
        {status.partsStatus?.ordered && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 flex gap-3">
            <FaBoxOpen className="text-amber-600 text-lg sm:text-xl shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-medium text-amber-900 text-sm leading-snug">
                {status.partsStatus.message}
              </p>
              {status.partsStatus.estimatedArrival && (
                <p className="text-xs text-amber-800 mt-1">
                  Estimated arrival: {formatDateOnly(status.partsStatus.estimatedArrival)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Estimated completion */}
        {status.estimatedCompletionAt && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 sm:p-4 mb-3 sm:mb-4">
            <p className="text-sm font-medium text-emerald-900">Estimated completion</p>
            <p className="text-emerald-800 mt-1 text-sm">
              {formatDateTime(status.estimatedCompletionAt)}
            </p>
          </div>
        )}

        {/* Desktop / tablet inline actions */}
        <ActionButtons
          onChat={openChat}
          onJobDetails={openJobDetails}
          className="hidden lg:flex"
        />

        <p className="text-center text-[11px] sm:text-xs text-gray-400 mt-4 sm:mt-6 pb-2 lg:pb-0">
          Status refreshes automatically every minute
        </p>
      </div>

      {/* Mobile sticky actions — above bottom nav */}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            className="checkout-light-page-bar fixed inset-x-0 z-[60] border-t border-gray-200 bg-white px-3 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] lg:hidden"
            style={{ bottom: 'calc(4.5rem + env(safe-area-inset-bottom, 0px))' }}
          >
            <div className="mx-auto w-full max-w-2xl">
              <ActionButtons onChat={openChat} onJobDetails={openJobDetails} />
            </div>
          </div>,
          document.body
        )}

      {/* Chat modal */}
      {chatOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40"
          onClick={() => {
            setChatOpen(false);
            setChatMessage('');
          }}
          role="presentation"
        >
          <div
            className="checkout-light-page bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-xl overflow-hidden max-h-[min(85dvh,520px)] flex flex-col"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            role="dialog"
            aria-labelledby="chat-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 shrink-0">
              <h3 id="chat-title" className="font-semibold text-gray-900 text-base">
                Message your garage
              </h3>
              <p className="text-xs text-gray-500 mt-1 leading-snug">
                {status.garage?.maskedAdvisorLabel || 'Service Advisor'} will respond via SparePe
                support.
              </p>
            </div>
            <div className="p-4 sm:p-5 flex-1 overflow-y-auto">
              <textarea
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                rows={4}
                placeholder="Ask about your booking, parts, or pickup time…"
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-900 resize-none focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400 min-h-[6rem]"
              />
              <div className="flex flex-col-reverse sm:flex-row gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setChatOpen(false);
                    setChatMessage('');
                  }}
                  className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 min-h-[2.75rem]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={chatSending}
                  onClick={handleSendMessage}
                  className="flex-1 py-2.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 inline-flex items-center justify-center gap-2 min-h-[2.75rem]"
                >
                  {chatSending ? <FaSpinner className="animate-spin" /> : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingStatus;
